import { useEffect, useState } from 'react';
import { api } from '../api/client.js';
import { colorForAccount } from '../charts/palette.js';
import { formatSubName } from '../lib/subName.js';
import { PostDrawer } from '../ui/PostDrawer.js';
import { StarBadge } from '../ui/StarButton.js';
import type { ChartRange, GlobalTopPost, PostChartPoint } from '@wml-crm/shared';

const RANGES: { key: ChartRange; label: string }[] = [
  { key: '1m', label: '1 мес' },
  { key: '3m', label: '3 мес' },
  { key: '6m', label: '6 мес' },
  { key: 'all', label: 'Всё' },
];

function formatDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y?.slice(2)}`;
}

function fmtConv(x: number | null): string {
  if (x == null) return '—';
  return `1/${Number.isInteger(x) ? x : x.toFixed(1)}`;
}

/** GlobalTopPost → PostChartPoint (для передачи в PostDrawer). Недостающие поля
 *  дозагрузятся drawer'ом через /api/posts/:id. */
function toChartPoint(p: GlobalTopPost): PostChartPoint {
  return {
    postId: p.postId,
    accountId: p.accountId,
    slaveName: p.slaveName,
    handle: p.handle,
    postUrl: p.postUrl,
    postDate: p.postDate,
    lastReportDate: p.lastReportDate,
    views: p.views,
    likes: 0,
    retweets: p.retweets,
    daysInWork: p.daysInWork,
    conversionDay1: p.conversionDay1,
    conversionDay2: null,
    conversionAvg: null,
    reuseCount: p.reuseCount,
  };
}

export function TopPostsPage() {
  const [posts, setPosts] = useState<GlobalTopPost[] | null>(null);
  const [range, setRange] = useState<ChartRange>('all');
  const [selected, setSelected] = useState<PostChartPoint | null>(null);

  useEffect(() => {
    setPosts(null);
    api<GlobalTopPost[]>(`/api/posts/top?limit=200&range=${range}`)
      .then(setPosts)
      .catch(() => setPosts([]));
  }, [range]);

  return (
    <div className="home-inner">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, letterSpacing: '-0.025em' }}>
            Топ-200 постов
          </h1>
          <div style={{ color: 'var(--text-subtle)', fontSize: 13, marginTop: 4 }}>
            по охватам · {posts?.length ?? 0} шт · клик — детали поста
          </div>
        </div>
        <div className="range-tabs">
          {RANGES.map((r) => (
            <button
              key={r.key}
              className={range === r.key ? 'active' : ''}
              onClick={() => setRange(r.key)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {posts === null ? (
        <div className="empty-state">Загрузка…</div>
      ) : posts.length === 0 ? (
        <div className="empty-state">Постов нет</div>
      ) : (
        <div className="card card-large top-posts">
          <ol className="top-posts-list">
            {posts.map((p, i) => {
              const sub = formatSubName(p.slaveName, p.modelAutoName);
              const color = colorForAccount(p.accountId);
              return (
                <li key={p.postId}>
                  <button
                    className="top-post-row"
                    style={{ gridTemplateColumns: '32px 10px minmax(0, 1fr) 90px 78px 60px 110px 42px 18px' }}
                    onClick={() => setSelected(toChartPoint(p))}
                  >
                    <span className="top-post-rank">{i + 1}</span>
                    <span className="acc-dot" style={{ background: color, borderColor: color }} />
                    <span className="top-post-acc">
                      <b>{p.modelDisplayName}</b>
                      {sub && <span style={{ color: 'var(--text-subtle)', marginLeft: 6 }}>{sub}</span>}
                    </span>
                    <span className="top-post-date">{formatDate(p.postDate)}</span>
                    <span className="top-post-days" title="Дней в работе">
                      {p.daysInWork} {p.daysInWork === 1 ? 'день' : (p.daysInWork < 5 ? 'дня' : 'дней')}
                    </span>
                    <span className="top-post-days" title="Конверт день-1">
                      {fmtConv(p.conversionDay1)}
                    </span>
                    <span className="top-post-views">{p.views.toLocaleString('ru-RU')}</span>
                    <StarBadge count={p.reuseCount ?? 0} />
                    <span className="top-post-open" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 6l6 6-6 6" />
                      </svg>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      )}

      {selected && (
        <PostDrawer
          point={selected}
          onClose={() => setSelected(null)}
          onReuseChanged={(postId, count) =>
            setPosts((prev) => prev ? prev.map((p) => p.postId === postId ? { ...p, reuseCount: count } : p) : prev)
          }
        />
      )}
    </div>
  );
}
